import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Observable, of } from 'rxjs';
import { apiEndpoints } from 'src/app/api-endpoints';
import { catchError, map } from 'rxjs/operators';
import { DynamicTableEntriesDto, DynamicTableEntriesResponse, DynamicTableSelectDto, FieldValuesResponse, GetValuesForFieldDto, DynamicTableFilters, SortCriteria, AggregationCriteria, ImportFromGCSDto, ExportToGCSDto, ExportToGCSResponse, DynamicTableRowCountDto, DynamicTableRowCountResponse, ClearDataDto, ClearDataResponse } from './models/dynamic-table.models';
import { getValuesForField } from 'src/assets/data/getValuesForField';

@Injectable({
  providedIn: 'root'
})
export class DynamicTableService {
  private apiUrl = environment.apiConfig.baseUri + apiEndpoints.dynamicTable;
  private dynamicTableApis = {
    addEntries: '/add-entries',
    addEntriesWithResult: '/add-entries-with-result',
    updateEntries: '/update-entries',
    updateEntriesWithResult: '/update-entries-with-result',
    selectEntries: '/select-entries',
    selectEntriesAdmin: '/select-entries-admin',
    getValuesForField: '/get-values-for-field',
    deleteEntries: '/delete-entries',
    importFromGCS: '/import-from-gcs',
    exportToGCS: '/export-to-gcs',
    getRowCount: '/get-row-count',
    clearData: '/clear-data',
    upsertEntries: '/upsert-entries'
  };

  constructor(
    private http: HttpClient,
  ) {}

  /**
   * Adds new entries for the given dataset.
   */
  addEntries(datasetId: number, rowData: any): Observable<boolean> {
      var request = new DynamicTableEntriesDto();
      request.datasetId = datasetId;
      request.rowData = rowData;

      return this.http.post<boolean>(
        `${this.apiUrl}${this.dynamicTableApis.addEntries}`,
        request
      ).pipe(
        catchError(() => {
          // Handle the error and return false
          return of(false);
        })
      );
  }

  /**
   * Adds new entries for the given dataset and returns the created records with auto-generated IDs.
   * Use this when you need the created record data (e.g., for chained saves that need the new primary key).
   * The response includes rowData with any server-side mutations (auto-generated GUIDs, auto-incremented IDs).
   */
  addEntriesWithResponse(datasetId: number, rowData: any): Observable<DynamicTableEntriesResponse> {
      var request = new DynamicTableEntriesDto();
      request.datasetId = datasetId;
      request.rowData = rowData;

      return this.http.post<DynamicTableEntriesResponse>(
        `${this.apiUrl}${this.dynamicTableApis.addEntriesWithResult}`,
        request
      );
  }

  /**
   * Updates existing entries for the given dataset.
   */
  updateEntries(datasetId: number, rowData: any): Observable<boolean> {
      var request = new DynamicTableEntriesDto();
      request.datasetId = datasetId;
      request.rowData = rowData;

      return this.http.post<boolean>(
        `${this.apiUrl}${this.dynamicTableApis.updateEntries}`,
        request
      ).pipe(
        catchError(() => {
          // Handle the error and return false
          return of(false);
        })
      );
  }

  /**
   * Updates existing entries for the given dataset and returns the updated records.
   * Use this when you need the updated record data (e.g., for chained saves or context updates).
   * The response includes rowData reflecting any server-side mutations.
   */
  updateEntriesWithResponse(datasetId: number, rowData: any): Observable<DynamicTableEntriesResponse> {
      var request = new DynamicTableEntriesDto();
      request.datasetId = datasetId;
      request.rowData = rowData;

      return this.http.post<DynamicTableEntriesResponse>(
        `${this.apiUrl}${this.dynamicTableApis.updateEntriesWithResult}`,
        request
      );
  }

  /**
   * Upserts entries for the given dataset.
   * Inserts new rows or updates existing ones based on primary key conflict.
   * Same payload shape as addEntries/updateEntries.
   */
  upsertEntries(datasetId: number, rowData: any): Observable<boolean> {
    var request = new DynamicTableEntriesDto();
    request.datasetId = datasetId;
    request.rowData = rowData;

    return this.http.post<boolean>(
      `${this.apiUrl}${this.dynamicTableApis.upsertEntries}`,
      request
    ).pipe(
      catchError(() => of(false))
    );
  }

   /**
   * Select the entries for the given dataset. If limit param is passed, it will return only that many entries, otherwise all.
   * @param datasetId The dataset ID to query
   * @param limit Maximum number of entries to return (-1 for all)
   * @param offset Optional offset for pagination (number of rows to skip)
   * @param filters Optional filters object where key is fieldId and value is array of values to filter by
   */
    selectEntries(datasetId: number, limit: number, offset?: number, filters?: DynamicTableFilters, distinct?: boolean, orderBy?: SortCriteria[], fields?: number[], groupBy?: number[], aggregations?: AggregationCriteria[]): Observable<DynamicTableEntriesResponse> {
      var request = new DynamicTableSelectDto();
      request.datasetId = datasetId;
      request.limit = limit;
      if (offset !== undefined) {
        request.offset = offset;
      }
      if (filters) {
        request.filters = filters;
      }
      if (distinct) {
        request.distinct = true;
      }
      if (orderBy && orderBy.length > 0) {
        request.orderBy = orderBy;
      }
      if (fields && fields.length > 0) {
        request.fields = fields;
      }
      if (groupBy && groupBy.length > 0) {
        request.groupBy = groupBy;

        // Aggregations only mean something alongside a groupBy - the API ignores them otherwise.
        if (aggregations && aggregations.length > 0) {
          request.aggregations = aggregations;
        }
      }

      return this.http.post<DynamicTableEntriesResponse>(
        `${this.apiUrl}${this.dynamicTableApis.selectEntries}`,
        request
      );
    }

    /**
   * Select entries bypassing row-level security. Only accessible by Admin/Owner users.
   * Used when configuring security access rules in the admin panel.
   */
    selectEntriesAdmin(datasetId: number, limit: number, offset?: number, filters?: DynamicTableFilters): Observable<DynamicTableEntriesResponse> {
      var request = new DynamicTableSelectDto();
      request.datasetId = datasetId;
      request.limit = limit;
      if (offset !== undefined) {
        request.offset = offset;
      }
      if (filters) {
        request.filters = filters;
      }

      return this.http.post<DynamicTableEntriesResponse>(
        `${this.apiUrl}${this.dynamicTableApis.selectEntriesAdmin}`,
        request
      );
    }

    /**
   * Get the total count of records in a dataset without loading the data.
   * This is more efficient than loading all records just to count them.
   * @param datasetId The dataset ID to query
   * @param filters Optional filters to count only matching records
   * @param groupBy Optional group-by fields - counts the groups, which is the row count of
   *                the matching grouped select rather than of the underlying rows
   */
    getDatasetCount(datasetId: number, filters?: DynamicTableFilters, groupBy?: number[]): Observable<number> {
      var request = new DynamicTableRowCountDto();
      request.datasetId = datasetId;
      if (filters) {
        request.filters = filters;
      }
      if (groupBy && groupBy.length > 0) {
        request.groupBy = groupBy;
      }

      return this.http.post<DynamicTableRowCountResponse>(
        `${this.apiUrl}${this.dynamicTableApis.getRowCount}`,
        request
      ).pipe(
        map(response => response.rowCount),
        catchError(() => {
          console.error('Error getting row count for dataset:', datasetId);
          return of(0);
        })
      );
    }

    /**
   * Select the entries for the given dataset. If limit param is passed, it will return only that many entries, otherwise all.
   */
    getValuesForDatasetPrimaryKey(datasetId: number): Observable<FieldValuesResponse> {
    var request = new GetValuesForFieldDto();
    request.datasetId = datasetId;

    return this.http.post<FieldValuesResponse>(
      `${this.apiUrl}${this.dynamicTableApis.getValuesForField}`,
      request
    );
  }

  /**
   * Get entries with specific field values for reference dropdown display
   * @param datasetId The dataset ID to query
   * @param referenceField The field that will be used as the value (usually primary key)
   * @param displayField The field that will be displayed to the user
   * @param limit Maximum number of entries to return (default: 200, -1 for all)
   */
  getDisplayValuesForReference(datasetId: number, referenceField: string, displayField: string, limit: number = 200): Observable<DynamicTableEntriesResponse> {
    return this.selectEntries(datasetId, limit);
  }

  /**
   * Deletes entries for the given dataset based on the provided row data.
   * The backend will identify which rows to delete based on primary keys.
   * @param datasetId The dataset ID
   * @param rowData Array of rows to delete (must contain primary key values)
   */
  deleteEntries(datasetId: number, rowData: any[]): Observable<boolean> {
    const request = new DynamicTableEntriesDto();
    request.datasetId = datasetId;
    request.rowData = rowData;

    return this.http.post<boolean>(
      `${this.apiUrl}${this.dynamicTableApis.deleteEntries}`,
      request
    ).pipe(
      catchError(() => {
        // Handle the error and return false
        return of(false);
      })
    );
  }

  /**
   * Imports data from a GCS file path. The backend will handle reading the file from GCS
   * and importing the data into the dataset.
   * @param datasetId The dataset ID to import data into
   * @param filePath The GCS file path (as returned from the file browser)
   * @param delimiter Optional delimiter for CSV files (default: comma). Use '\t' for tab.
   */
  importFromGCS(datasetId: number, filePath: string, delimiter?: string): Observable<boolean> {
    const request = new ImportFromGCSDto();
    request.datasetId = datasetId;
    request.filePath = filePath;
    if (delimiter) {
      // Convert '\t' string to actual tab character
      request.delimiter = delimiter === '\\t' ? '\t' : delimiter;
    }

    return this.http.post<boolean>(
      `${this.apiUrl}${this.dynamicTableApis.importFromGCS}`,
      request
    ).pipe(
      catchError(() => {
        // Handle the error and return false
        return of(false);
      })
    );
  }

  /**
   * Exports rows from a dataset to a file in the app's GCS bucket. Takes the same
   * filters, column selection and grouping a select does, so the file can hold exactly
   * what the caller is looking at rather than the whole table.
   * @param applicationId The application ID
   * @param datasetId The dataset ID to export
   * @param filePath The destination GCS file path
   * @param fileFormat Optional format ('csv' or 'json'), inferred from extension if omitted
   * @param delimiter Optional delimiter for CSV (default: comma)
   * @param filters Optional filters - only matching rows are exported
   * @param fields Optional column projection (by fieldId). Ignored when grouping.
   * @param groupBy Optional group-by fields - the file then holds one row per group
   * @param aggregations Optional aggregates exported alongside the groupBy columns
   */
  exportToGCS(
    applicationId: number,
    datasetId: number,
    filePath: string,
    fileFormat?: string,
    delimiter?: string,
    filters?: DynamicTableFilters,
    fields?: number[],
    groupBy?: number[],
    aggregations?: AggregationCriteria[]
  ): Observable<ExportToGCSResponse> {
    const request = new ExportToGCSDto();
    request.applicationId = applicationId;
    request.datasetId = datasetId;
    request.filePath = filePath;
    if (fileFormat) {
      request.fileFormat = fileFormat;
    }
    if (delimiter) {
      request.delimiter = delimiter === '\\t' ? '\t' : delimiter;
    }
    if (filters) {
      request.filters = filters;
    }
    if (fields && fields.length > 0) {
      request.fields = fields;
    }
    if (groupBy && groupBy.length > 0) {
      request.groupBy = groupBy;

      if (aggregations && aggregations.length > 0) {
        request.aggregations = aggregations;
      }
    }

    return this.http.post<ExportToGCSResponse>(
      `${this.apiUrl}${this.dynamicTableApis.exportToGCS}`,
      request
    );
  }

  /**
   * Deletes rows from a dataset where a specific field matches a given value.
   * @param datasetId The dataset ID
   * @param fieldId The field ID to match on
   * @param value The value to match for deletion
   */
  clearData(datasetId: number, fieldId: number, value: string): Observable<ClearDataResponse> {
    const request = new ClearDataDto();
    request.datasetId = datasetId;
    request.fieldId = fieldId;
    request.value = value;

    return this.http.post<ClearDataResponse>(
      `${this.apiUrl}${this.dynamicTableApis.clearData}`,
      request
    );
  }
}
